// src/modules/Shop/pages/Category.js
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaFilter, FaTimes, FaChevronDown } from 'react-icons/fa';
import { supabase } from '../../../config/supabase';
import ProductGrid from '../components/ProductGrid';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #666;
  margin-bottom: 24px;

  a {
    color: #666;
    text-decoration: none;

    &:hover {
      color: #00D4AA;
    }
  }

  span {
    color: #999;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;

  h1 {
    font-size: 32px;
    font-weight: 700;
    color: #111;
    margin-bottom: 8px;
  }

  p {
    color: #666;
    font-size: 14px;
  }
`;

const LayoutContainer = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.aside`
  @media (max-width: 900px) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: white;
    z-index: 200;
    padding: 24px;
    overflow-y: auto;
  }
`;

const SidebarHeader = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    h2 {
      font-size: 18px;
      font-weight: 600;
    }

    button {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #666;
    }
  }
`;

const FilterSection = styled.div`
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const FilterTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #111;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;

  svg {
    font-size: 12px;
    color: #999;
    transition: transform 200ms;
    transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const FilterOptions = styled.div`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 12px;
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #666;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    accent-color: #00D4AA;
  }

  &:hover {
    color: #111;
  }
`;

const PriceRange = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  input {
    flex: 1;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #00D4AA;
    }
  }

  span {
    color: #999;
  }
`;

const ApplyFiltersButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 16px;

  &:hover {
    opacity: 0.9;
  }
`;

const MainContent = styled.main``;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ResultsCount = styled.span`
  font-size: 14px;
  color: #666;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MobileFilterButton = styled.button`
  display: none;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;

  @media (max-width: 900px) {
    display: flex;
  }

  &:hover {
    border-color: #111;
  }
`;

const SortSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00D4AA;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 48px;
`;

const PageButton = styled.button`
  min-width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.$active ? '#00D4AA' : '#ddd'};
  background: ${props => props.$active ? '#00D4AA' : 'white'};
  color: ${props => props.$active ? '#000' : '#666'};
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 200ms;

  &:hover:not(:disabled) {
    border-color: #00D4AA;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Category = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState({
    categories: true,
    price: true,
    availability: true
  });

  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const sortBy = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');
  const perPage = 12;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [slug, sortBy, page, selectedCategories, inStockOnly]);

  const fetchCategories = async () => {
    const { data } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    setCategories(data || []);
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      // Buscar categoria atual
      if (slug && slug !== 'todos') {
        const { data: catData } = await supabase
          .from('shop_categories')
          .select('*')
          .eq('slug', slug)
          .single();

        setCategory(catData);
      } else {
        setCategory(null);
      }

      // Query base
      let query = supabase
        .from('shop_products')
        .select('*, category:shop_categories(name, slug)', { count: 'exact' })
        .eq('is_active', true);

      // Filtrar por categoria
      if (slug && slug !== 'todos') {
        const { data: catData } = await supabase
          .from('shop_categories')
          .select('id')
          .eq('slug', slug)
          .single();

        if (catData) {
          query = query.eq('category_id', catData.id);
        }
      }

      // Filtrar por estoque
      if (inStockOnly) {
        query = query.gt('stock', 0);
      }

      // Ordenacao
      switch (sortBy) {
        case 'price_asc':
          query = query.order('price', { ascending: true });
          break;
        case 'price_desc':
          query = query.order('price', { ascending: false });
          break;
        case 'name':
          query = query.order('name', { ascending: true });
          break;
        case 'newest':
        default:
          query = query.order('created_at', { ascending: false });
      }

      // Paginacao
      const from = (page - 1) * perPage;
      const to = from + perPage - 1;
      query = query.range(from, to);

      const { data, count, error } = await query;

      if (error) throw error;

      setProducts(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (e) => {
    setSearchParams({ sort: e.target.value, page: '1' });
  };

  const handlePageChange = (newPage) => {
    setSearchParams({ sort: sortBy, page: String(newPage) });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(totalCount / perPage);

  const toggleFilter = (filterName) => {
    setFiltersOpen(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  return (
    <PageContainer>
      <Breadcrumb>
        <Link to="/">Inicio</Link>
        <span>/</span>
        {category ? (
          <span>{category.name}</span>
        ) : (
          <span>Todos os Produtos</span>
        )}
      </Breadcrumb>

      <PageHeader>
        <h1>{category?.name || 'Todos os Produtos'}</h1>
        {category?.description && <p>{category.description}</p>}
      </PageHeader>

      <LayoutContainer>
        <Sidebar $isOpen={sidebarOpen}>
          <SidebarHeader>
            <h2>Filtros</h2>
            <button onClick={() => setSidebarOpen(false)}>
              <FaTimes />
            </button>
          </SidebarHeader>

          <FilterSection>
            <FilterTitle $isOpen={filtersOpen.categories} onClick={() => toggleFilter('categories')}>
              Categorias
              <FaChevronDown />
            </FilterTitle>
            <FilterOptions $isOpen={filtersOpen.categories}>
              {categories.map(cat => (
                <FilterOption key={cat.id}>
                  <input
                    type="checkbox"
                    checked={slug === cat.slug || selectedCategories.includes(cat.id)}
                    onChange={() => {
                      if (slug !== cat.slug) {
                        window.location.href = `/categoria/${cat.slug}`;
                      }
                    }}
                  />
                  {cat.name}
                </FilterOption>
              ))}
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle $isOpen={filtersOpen.price} onClick={() => toggleFilter('price')}>
              Preco
              <FaChevronDown />
            </FilterTitle>
            <FilterOptions $isOpen={filtersOpen.price}>
              <PriceRange>
                <input
                  type="number"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                />
                <span>-</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                />
              </PriceRange>
            </FilterOptions>
          </FilterSection>

          <FilterSection>
            <FilterTitle $isOpen={filtersOpen.availability} onClick={() => toggleFilter('availability')}>
              Disponibilidade
              <FaChevronDown />
            </FilterTitle>
            <FilterOptions $isOpen={filtersOpen.availability}>
              <FilterOption>
                <input
                  type="checkbox"
                  checked={inStockOnly}
                  onChange={(e) => setInStockOnly(e.target.checked)}
                />
                Apenas em estoque
              </FilterOption>
            </FilterOptions>
          </FilterSection>

          <ApplyFiltersButton onClick={() => {
            fetchProducts();
            setSidebarOpen(false);
          }}>
            Aplicar Filtros
          </ApplyFiltersButton>
        </Sidebar>

        <MainContent>
          <Toolbar>
            <ResultsCount>
              {totalCount} {totalCount === 1 ? 'produto encontrado' : 'produtos encontrados'}
            </ResultsCount>

            <ToolbarRight>
              <MobileFilterButton onClick={() => setSidebarOpen(true)}>
                <FaFilter /> Filtros
              </MobileFilterButton>

              <SortSelect value={sortBy} onChange={handleSortChange}>
                <option value="newest">Mais recentes</option>
                <option value="price_asc">Menor preco</option>
                <option value="price_desc">Maior preco</option>
                <option value="name">Nome A-Z</option>
              </SortSelect>
            </ToolbarRight>
          </Toolbar>

          <ProductGrid products={products} loading={loading} />

          {totalPages > 1 && (
            <Pagination>
              <PageButton
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                Anterior
              </PageButton>

              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <PageButton
                    key={pageNum}
                    $active={page === pageNum}
                    onClick={() => handlePageChange(pageNum)}
                  >
                    {pageNum}
                  </PageButton>
                );
              })}

              <PageButton
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
              >
                Proximo
              </PageButton>
            </Pagination>
          )}
        </MainContent>
      </LayoutContainer>
    </PageContainer>
  );
};

export default Category;
